import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "motion/react";
import { easeOut, transitionSoft, viewportOnce } from "../animations";

const DOCUMENTS = [
  { labelKey: "privacyApp" },
  { labelKey: "license" },
  { labelKey: "publicOffer" },
  { labelKey: "cardPrivacy" },
  { labelKey: "tariff" },
  { labelKey: "vertualCard" },
  { labelKey: "depositorInformationSheet" },
  { labelKey: "ICBMobile&CardsPrivacyPolicy" },
];

const DOCUMENTS_OLD = [{ labelKey: "privacyAppArchive" }];

function DocumentLinksList({ t, docs }) {
  return (
    <ul className="space-y-[20px] md:w-[360px] lg:w-[480px] xl:w-[600px]">
      {docs.map((doc) => (
        <li
          key={doc.labelKey}
          className="border-b-[2px] pb-[16px] md:border-hidden md:pb-[0]"
        >
          <motion.a
            href={t(`documentation.paths.${doc.labelKey}`)}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ x: 4, color: "#da0011" }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
            className="flex items-start text-justify text-[14px] font-medium leading-[25px] text-inherit"
          >
            <img
              src="./img/file.svg"
              alt=""
              className="mr-[6px] h-[25px] w-[20px] shrink-0"
            />
            {t(`documentation.links.${doc.labelKey}`)}
          </motion.a>
        </li>
      ))}
    </ul>
  );
}

function Documentation() {
  const { t } = useTranslation();
  const [archiveOpen, setArchiveOpen] = useState(false);

  return (
    <motion.section
      id="doc"
      className="m-auto px-4 lg:max-w-[1024px] xl:max-w-[1280px]"
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewportOnce}
      transition={{ ...transitionSoft, duration: 0.55 }}
    >
      <motion.h4
        className="mb-[30px] mt-[50px] text-[20px] font-medium uppercase text-[#da0011] md:mb-[66px] md:mt-[40px] lg:mt-[60px] xl:mt-[80px] md:text-[26px] lg:text-[30px] xl:text-[36px]"
        initial={{ opacity: 0, x: -12 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={viewportOnce}
        transition={{ ...transitionSoft, delay: 0.04 }}
      >
        {t("documentation.heading")}
      </motion.h4>
      <div className="flex flex-col justify-between md:flex-row">
        <DocumentLinksList t={t} docs={DOCUMENTS} />
      </div>

      <motion.div
        layout
        className="mt-[24px] overflow-hidden rounded-2xl border border-neutral-200/90 bg-white shadow-[0_4px_28px_rgba(218,0,17,0.07),0_1px_4px_rgba(0,0,0,0.04)] md:mt-[40px]"
      >
        <motion.button
          type="button"
          layout
          onClick={() => setArchiveOpen((v) => !v)}
          aria-expanded={archiveOpen}
          whileTap={{ scale: 0.995 }}
          className="flex w-full items-center justify-between gap-3 bg-gradient-to-r from-[#da0011]/[0.08] via-white to-neutral-50/80 px-5 py-[16px] text-left text-[14px] font-medium text-neutral-800 transition-all duration-200 hover:from-[#da0011]/[0.12] hover:via-[#fff8f8] hover:to-neutral-50 md:px-7 md:py-[18px]"
        >
          <span className="uppercase tracking-[0.06em]">
            {t("documentation.archive")}
          </span>
          <motion.span
            animate={{ rotate: archiveOpen ? 180 : 0 }}
            transition={{ duration: 0.28, ease: easeOut }}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#da0011]/10 text-[10px] text-[#da0011]"
            aria-hidden
          >
            ▼
          </motion.span>
        </motion.button>
        <AnimatePresence initial={false}>
          {archiveOpen ? (
            <motion.div
              key="archive"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.32, ease: easeOut }}
              className="overflow-hidden border-t border-neutral-100"
            >
              <div className="bg-gradient-to-b from-neutral-50/60 to-white px-5 pb-[24px] pt-[20px] md:px-7">
                <DocumentLinksList t={t} docs={DOCUMENTS_OLD} />
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.div>
    </motion.section>
  );
}

export default Documentation;
